import { defineStore } from 'pinia';
import { Modals } from '@/js/quests/dashboard/types/enums';

export enum ModalEnum {
    ADD_PASSWORD = 'add-password',
    AIRDROP_CLAIM = 'airdrop-claim',
    BADGE = 'badge',
    BROWSER_NOTIFICATIONS = 'browser-notifications',
    BONUS_CONGRATULATIONS = 'bonus-congratulations',
    BYBIT_DEAL_NOTIFICATION = 'bybit-deal-notification',
    BYBIT_DEAL_REWARD_CLAIMED = 'bybit-deal-reward-claimed',
    COINBASE_AUTH = 'coinbase-auth',
    COINBASE_AUTH_INTRO = 'coinbase-auth-intro',
    CONNECTED_SOCIAL_ACCOUNT = 'connected-social-account',
    CONNECTED_WALLET = 'connected-wallet',
    CUSTOM_ERROR = 'custom-error',
    DEACTIVATE_ACCOUNT = 'deactivate-account',
    DELETE_GUILD_CONFIRMATION = 'delete-guild-confirmation',
    DOWNLOAD_APP = 'download-app',
    EMAIL_VERIFY = 'email-verify',
    EXAM_RETRY = 'exam-retry',
    EXTENSION_CONNECTED = 'extension-connected',
    FEEDBACK = 'feedback',
    FILTERS = 'filters',
    FORCE_EMAIL_VERIFICATION = 'force-email-verification',
    GUILD_UNFOLLOW = 'guild-unfollow',
    GUILDS_SUGGESTIONS = 'guilds-suggestions',
    HEART_RECOVERY = 'heart-recovery',
    HOW_TO_WITHDRAW = 'how-to-withdraw',
    INPUT = 'input',
    INTRODUCTION = 'introduction',
    INVITE_FRIENDS = 'invite-friends',
    LEAVING_CONFIRMATION = 'leaving-confirmation',
    LUCKY_DRAW_RESTART = 'lucky-draw-restart',
    LUCKY_DRAW_WINNERS = 'lucky-draw-winners',
    METAMASK_AUTH = 'metamask-auth',
    METAMASK_AUTH_INTRO = 'metamask-auth-intro',
    NFT_CERTIFICATE = 'nft-certificate',
    NFT_MINT_SUCCESS = 'nft-mint-success',
    PASSWORD_CHANGE = 'password-change',
    PASSWORD_EXIST = 'password-exist',
    RATE_BONUS = 'rate-bonus',
    REGISTER_BONUS = 'register-bonus',
    REPORT = 'report',
    RETURN_TO_MOBILE_APP = 'return-to-mobile-app',
    TANGIBLE_REWARD = 'tangible-reward',
    TWITTER_APP_BROWSER = 'twitter-app-browser',
    SOCIAL_ACCOUNT_ERROR = 'social-account-error',
    SKIP_TASK = 'skip-task',
    STARTED_ROUND = 'started-round',
    UNAVAILABLE_ROUND = 'unavailable-round',
    UNLOCK_STUDIO_STEP = 'unlock-studio-step',
    VIDEO = 'video',
    VISIT_SITE = 'visit-site',
    UNLOCKED_STUDIO = 'unlocked-studio',
    USER_SOCIAL_ACCOUNTS = 'useer-social-accounts',
    USER_WALLETS = 'user-wallets',
}

interface ModalState {
    modals: Record<ModalEnum, boolean>;
    authModals: {
        showAuth: boolean,
        showWalletConnect: boolean,
    },
    showExitAnimation: boolean,
    isOnboarding: boolean,
    skipNotifications: boolean,
    modalPromiseResolver: ((value?: unknown) => void) | null;
}

type ModalKeys = keyof ModalState['authModals'];

const createModalRecord = (): Record<ModalEnum, boolean> => Object.values(ModalEnum).reduce((acc, key) => ({ ...acc, [key]: false }), {} as Record<ModalEnum, boolean>);

export const useModalStore = defineStore({
    id: 'modalStore',
    state: (): ModalState => ({
        modals: createModalRecord(),
        authModals: {
            showAuth: false,
            showWalletConnect: false,
        },
        showExitAnimation: false,
        isOnboarding: false,
        skipNotifications: false,
        modalPromiseResolver: null,
    }),
    actions: {
        hideAllModals(value: boolean) {
            if (!value) {
                return;
            }

            const authModals = this.authModals;
            // eslint-disable-next-line guard-for-in, no-restricted-syntax
            for (const authModal in authModals) {
                authModals[authModal] = false;
            }

            const modals = this.modals;
            // eslint-disable-next-line guard-for-in, no-restricted-syntax
            for (const modal in modals) {
                modals[modal] = false;
            }
        },
        setModalVisibility(value: boolean, modalName: ModalKeys, hideOtherModals = true, timeout = 500) {
            this.hideAllModals(hideOtherModals);

            if (!value) {
                this.showExitAnimation = true;
                setTimeout(() => {
                    this.authModals[modalName] = false;
                    this.showExitAnimation = false;
                }, timeout);
            } else {
                this.authModals[modalName] = value;
            }
        },
        toggleModal(modalKey: ModalEnum, value: boolean) {
            this.modals[modalKey] = value;
        },
        setAuthModal(value: boolean) {
            // IMPORTANT: Do not use hideAllModals - issue #3952
            this.authModals.showAuth = value;
        },
        setWalletConnectModal(value: boolean, showAnimation = true) {
            if (showAnimation) {
                this.setModalVisibility(value, Modals.WALLET_CONNECT);
            } else {
                this.authModals.showWalletConnect = value;
            }
        },
        resolveReturnToMobileAppResolver() {
            if (!this.modals[ModalEnum.RETURN_TO_MOBILE_APP] && this.modalPromiseResolver) {
                this.modalPromiseResolver();
                this.modalPromiseResolver = null;
            }
        },
        async waitForReturnToMobileAppModalClosure() {
            if (!this.modals[ModalEnum.RETURN_TO_MOBILE_APP]) {
                return Promise.resolve();
            }

            return new Promise((resolve) => {
                this.modalPromiseResolver = resolve;
            });
        },
        setDownloadAppModal(value: boolean, isOnboarding = false) {
            this.isOnboarding = isOnboarding;
            this.toggleModal(ModalEnum.DOWNLOAD_APP, value);
        },
    },
});
